import { useMemo } from 'react';
import { Column, Row } from '@tanstack/react-table';

import { isRelativeModificationForWebEDI } from 'services/webedi/modificationGroupHelpers';
import { getEmbeddedTableRowRowSpan } from 'components/DataTable/helpers';
import { DisplayModes, RowWithEmbeddedTableRowInfo, WebEDIRowData } from 'components/DataTable/Types';

import { buildEmbeddedTableCellId, getFieldId } from './fieldIdHelpers';

export const useFieldId = <TData = WebEDIRowData>(
  cellId: string,
  column: Column<TData>,
  dataPrefix: string | undefined,
  rowWithEmbeddedTableRowInfo?: RowWithEmbeddedTableRowInfo<TData>,
  row?: Row<TData>,
  displayMode?: DisplayModes,
): string => {
  return useMemo(() => {
    const parentColumnPath = column.parent?.id;
    const rowSpanInfo = getEmbeddedTableRowRowSpan(rowWithEmbeddedTableRowInfo, parentColumnPath);
    const arrayItemIndex = rowSpanInfo?.itemIndex ?? -1;

    let formattedCellId = cellId;

    if (rowSpanInfo && arrayItemIndex >= 0 && parentColumnPath) {
      formattedCellId = buildEmbeddedTableCellId({ cellId, arrayItemIndex });
    }

    const fieldId = getFieldId({
      columnDef: column.columnDef,
      dataPrefix,
      cellId: formattedCellId,
      parentColumnPath,
    });

    if (row && displayMode) {
      const isLineItems = dataPrefix?.includes('line_items');
      const isRelativeModificationRow = isRelativeModificationForWebEDI(
        fieldId,
        row.original as WebEDIRowData,
        displayMode,
        Boolean(isLineItems),
      );

      if (isRelativeModificationRow) {
        return fieldId.replace(/(_amount|\.amount)$/, (match) => match.replace('amount', 'percentage'));
      }
    }

    return fieldId;
  }, [cellId, column, dataPrefix, rowWithEmbeddedTableRowInfo, row, displayMode]);
};
